import React, { useEffect, useRef, useState } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from './firebaseInit';
import { useAuth } from './contexts/AuthContext';
import { useNotification } from './contexts/NotificationContext';
import { cleanupGlobalSetup } from './globalSetup';
import LoadingSpinner from './components/shared/LoadingSpinner';
import App from './App';

export default function SessionContainer() {
  const { userId, userEmail, isAuthReady } = useAuth();
  const previousSessionRef = useRef(null);
  const cleanupTimeoutRef = useRef(null);
    
  // Create session identifier
  const sessionKey = userId ? `session-${userId}` : 'no-session';
    
  // Cleanup previous session before new one starts
  useEffect(() => {
    const previousSession = previousSessionRef.current;
        
    if (previousSession && previousSession !== sessionKey) {
      console.log('🔐 SESSION: Cleaning up previous session:', previousSession);
            
      // Clear any pending cleanup timeout
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
            
      // Cleanup global state
      cleanupGlobalSetup();
            
      // Clear URL parameters that might persist
      if (window.location.search.includes('deactivated=true') || window.location.search.includes('session_conflict=true')) {
        const url = new URL(window.location);
        url.searchParams.delete('deactivated');
        url.searchParams.delete('session_conflict');
        window.history.replaceState({}, '', url);
        console.log('🔐 SESSION: Cleared deactivated/session_conflict URL parameter');
      }
            
      // Clear any other user-specific URL parameters
      const url = new URL(window.location);
      const userSpecificParams = ['user', 'team', 'session'];
      let clearedParams = false;
            
      userSpecificParams.forEach(param => {
        if (url.searchParams.has(param)) {
          url.searchParams.delete(param);
          clearedParams = true;
        }
      });
            
      // Preserve the current pathname if it's a valid app route
      const validRoutes = ['/call-script', '/my-scripts', '/command-center', '/dashboard', '/admin-panel',
        '/team-manager', '/ai-call-evaluator', '/settings',
        '/admin-templates', '/problem-analyzer', '/team',
        '/help-center', '/marketplace', '/leads'];
      const currentPath = window.location.pathname;
            
      // If current path is valid, preserve it during session cleanup
      // This ensures users stay on the same page when switching accounts
      if (validRoutes.includes(currentPath) || currentPath.startsWith('/team/') || currentPath.startsWith('/marketplace/')) {
        console.log('🔐 SESSION: Preserving valid route:', currentPath);
      } else if (currentPath !== '/' && !currentPath.startsWith('/flowchart-editor/')) {
        // Reset invalid routes to home page
        console.log('🔐 SESSION: Resetting invalid route to home:', currentPath);
        window.history.replaceState({}, '', '/');
      }
            
      if (clearedParams) {
        window.history.replaceState({}, '', url);
        console.log('🔐 SESSION: Cleared user-specific URL parameters');
      }
            
      // Small delay to ensure cleanup completes before new session starts
      cleanupTimeoutRef.current = setTimeout(() => {
        console.log('🔐 SESSION: Cleanup completed, ready for new session');
      }, 100);
    }
        
    previousSessionRef.current = sessionKey;
        
    // Cleanup timeout on unmount
    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [sessionKey]);
    
  // Log session changes (only when sessionKey changes, not on every render)
  useEffect(() => {
    if (userId) {
      console.log('🔐 SESSION: New session started', {
        userId,
        userEmail: userEmail ? '[REDACTED]' : 'null',
        sessionKey,
        timestamp: new Date().toISOString()
      });
            
      // SECURITY (L1): Minimize PII in window object — use masked email for logging only
      window.currentUserEmail = userEmail ?
        userEmail.split('@')[0].substring(0, 3) + '***@' + userEmail.split('@')[1] : null;
      // Note: userTeamId will be set by App component when it mounts
    } else {
      console.log('🔐 SESSION: No active session (logged out)');
      // Clear on logout
      window.currentUserEmail = null;
      window.currentUserTeamId = null;
    }
  }, [sessionKey]); // Only depend on sessionKey, not userId/userEmail
    
  // Development mode session inspector
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.SESSION_DEBUG = {
        currentKey: sessionKey,
        mountCount: ++window._mountCounter || 1,
        userId,
        userEmail: userEmail ? '[REDACTED]' : 'null',
        isAuthReady,
        timestamp: new Date().toISOString()
      };
            
      console.log('🔧 DEV: Session debug info updated:', window.SESSION_DEBUG);
    }
  }, [sessionKey]); // Only depend on sessionKey to prevent excessive updates
    
  // Global AI script generation tracker.
  // Monitors active generation and shows toast when complete, even on other pages.
  // Handles both in-session navigation AND page refresh recovery.
  const { showSuccess: genShowSuccess, showError: genShowError } = useNotification();
  const genListenerRef = useRef(null);
  const [trackGeneration, setTrackGeneration] = useState(() => {
    try { return !!localStorage.getItem('activeScriptGeneration'); } catch { return false; }
  });

  useEffect(() => {
    const handler = () => setTrackGeneration(true);
    window.addEventListener('scriptGenerationStarted', handler);
    return () => window.removeEventListener('scriptGenerationStarted', handler);
  }, []);

  useEffect(() => {
    if (genListenerRef.current) {
      genListenerRef.current();
      genListenerRef.current = null;
    }
    if (!trackGeneration) return;

    let stored;
    try { stored = JSON.parse(localStorage.getItem('activeScriptGeneration')); } catch { /* ok */ }
    if (!stored?.teamId || !stored?.startedAt) {
      setTrackGeneration(false);
      return;
    }

    if (Date.now() - stored.startedAt > 15 * 60 * 1000) {
      try { localStorage.removeItem('activeScriptGeneration'); } catch { /* ok */ }
      setTrackGeneration(false);
      return;
    }

    const genRef = collection(
      db,
      `artifacts/my-real-estate-assistant/teams/${stored.teamId}/script-generations`
    );
    const q = query(genRef, orderBy('createdAt', 'desc'), limit(1));

    genListenerRef.current = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) return;
      const data = snapshot.docs[0].data();
      const docTime = data.createdAt?.toDate?.()?.getTime?.() || (data.createdAt?.seconds || 0) * 1000;

      if (docTime < stored.startedAt - 60000) return;

      if (data.status === 'completed') {
        const stillActive = localStorage.getItem('activeScriptGeneration');
        if (!stillActive) { setTrackGeneration(false); return; }
        try { localStorage.removeItem('activeScriptGeneration'); } catch { /* ok */ }
        setTrackGeneration(false);
        if (!window.location.pathname.includes('/my-scripts')) {
          genShowSuccess('Your AI script is ready! Go to My Scripts → AI Script Creator to review it.');
        }
      } else if (data.status === 'error') {
        try { localStorage.removeItem('activeScriptGeneration'); } catch { /* ok */ }
        setTrackGeneration(false);
        genShowError('AI script generation failed. Try again in the AI Script Builder.');
      }
    }, (err) => {
      console.warn('Generation tracker listener error:', err.message);
      setTrackGeneration(false);
    });

    return () => {
      if (genListenerRef.current) { genListenerRef.current(); genListenerRef.current = null; }
    };
  }, [trackGeneration, genShowSuccess, genShowError]);

  // Track session transitions to apply a fade-in, masking the visual flash
  // caused by the key-based remount of the entire App tree.
  const [fadeIn, setFadeIn] = useState(false);
  const prevSessionKeyRef = useRef(sessionKey);

  useEffect(() => {
    if (prevSessionKeyRef.current !== sessionKey) {
      const wasLoggedOut = prevSessionKeyRef.current === 'no-session';
      prevSessionKeyRef.current = sessionKey;
      if (wasLoggedOut && sessionKey !== 'no-session') {
        setFadeIn(true);
        const timer = setTimeout(() => setFadeIn(false), 400);
        return () => clearTimeout(timer);
      }
    }
  }, [sessionKey]);

  if (!isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" role="status" aria-live="polite">
        <LoadingSpinner size="large" message="Initializing authentication..." />
      </div>
    );
  }
    
  // Force complete remount when session key changes
  return (
    <div className={fadeIn ? 'session-fade-in' : undefined}>
      <App key={sessionKey} />
    </div>
  );
}
